import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { apiService } from '../services/api';

const Settings = () => {
  const [settings, setSettings] = useState({
    daily_cycle_limit: 2,
    min_emails_per_cycle: 5,
    max_emails_per_cycle: 10,
    office_start_hour: 9,
    office_end_hour: 18,
    min_cycle_interval_minutes: 30,
    max_cycle_interval_minutes: 120,
    // Email delay settings (human-like timing)
    min_email_delay_minutes: 3,
    max_email_delay_minutes: 5,
    // Reply system settings
    reply_probability: 40,
    min_reply_delay_minutes: 30,
    max_reply_delay_minutes: 180,
    max_replies_per_cycle: 3,
    enable_reply_processing: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loadingScheduler, setLoadingScheduler] = useState(false);
  const [clearingScheduler, setClearingScheduler] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  // Re-fetch settings when component becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('🔄 Page became visible, refreshing settings...');
        fetchSettings();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const fetchSettings = async () => {
    try {
      console.log('🔄 Fetching settings...');
      const response = await apiService.getSettings();
      console.log('✅ Settings fetched:', response);
      
      // Handle nested response structure
      let settingsData = response;
      if (response.data && response.data.success) {
        settingsData = response.data.data || response.data;
      } else if (response.data) {
        settingsData = response.data;
      }
      
      // Only update settings with actual setting values (exclude metadata)
      const settingFields = [
        'daily_cycle_limit', 'min_emails_per_cycle', 'max_emails_per_cycle',
        'office_start_hour', 'office_end_hour', 'min_cycle_interval_minutes', 'max_cycle_interval_minutes',
        // Email delay settings
        'min_email_delay_minutes', 'max_email_delay_minutes',
        // Reply system settings
        'reply_probability', 'min_reply_delay_minutes', 'max_reply_delay_minutes', 
        'max_replies_per_cycle', 'enable_reply_processing'
      ];
      
      const filteredSettings = {};
      settingFields.forEach(field => {
        if (settingsData.hasOwnProperty(field) && settingsData[field] !== null && settingsData[field] !== undefined) {
          filteredSettings[field] = settingsData[field];
        }
      });
      
      setSettings(prev => ({ ...prev, ...filteredSettings }));
      setLastUpdated(new Date());
      setLoading(false);
    } catch (error) {
      console.error('❌ Failed to fetch settings:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
      toast.error(`Failed to load settings: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    await fetchSettings();
    toast.info('Settings refreshed from database');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      console.log('💾 Saving settings:', settings);
      const response = await apiService.updateSettings(settings);
      console.log('✅ Settings saved:', response);
      toast.success(response.message || 'Settings saved successfully!');
      
      // Re-fetch settings from database to ensure consistency
      console.log('🔄 Re-fetching settings to ensure consistency...');
      await fetchSettings();
      console.log('✅ Settings refreshed from database');
      
    } catch (error) {
      console.error('❌ Failed to save settings:', error);
      console.error('❌ Save error details:', error.response?.data || error.message);
      toast.error(`Failed to save settings: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: field === 'enable_reply_processing' ? value : (parseInt(value) || value)
    }));
  };

  const fetchSchedulerStatus = async () => {
    setLoadingScheduler(true);
    try {
      console.log('🔄 Fetching scheduler status...');
      const response = await apiService.getPersistentSchedulerStatus();
      console.log('✅ Scheduler status fetched:', response);
      
      // Handle nested response structure
      const statusData = response.data?.data || response.data || response;
      setSchedulerStatus(statusData);
    } catch (error) {
      console.error('❌ Failed to fetch scheduler status:', error);
      toast.error(`Failed to load scheduler status: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
    } finally {
      setLoadingScheduler(false);
    }
  };

  const handleClearScheduler = async () => {
    if (!window.confirm('Are you sure you want to clear all persistent scheduled emails? This action cannot be undone.')) {
      return;
    }

    setClearingScheduler(true);
    try {
      console.log('🗑️ Clearing persistent scheduler...');
      const response = await apiService.clearPersistentScheduler();
      console.log('✅ Scheduler cleared:', response);
      toast.success(response.message || 'Persistent scheduler cleared successfully!');
      
      // Refresh scheduler status after clearing
      await fetchSchedulerStatus();
    } catch (error) {
      console.error('❌ Failed to clear scheduler:', error);
      toast.error(`Failed to clear scheduler: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
    } finally {
      setClearingScheduler(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6 fade-in pt-16">
      <div className="px-4 sm:px-0">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500 text-sm sm:text-base">Configure your email warmup system settings</p>
      </div>

      {/* Current Settings Summary */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 sm:p-6 mx-4 sm:mx-0">
        <div className="flex items-center mb-3">
          <svg className="h-6 w-6 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <h3 className="text-lg font-semibold text-blue-900">Current Active Configuration</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-sm">
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="text-gray-600 text-xs mb-1">Daily Cycles</div>
            <div className="text-2xl font-bold text-blue-600">{settings.daily_cycle_limit}</div>
            <div className="text-gray-500 text-xs mt-1">per day</div>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="text-gray-600 text-xs mb-1">Emails/Cycle</div>
            <div className="text-2xl font-bold text-green-600">{settings.min_emails_per_cycle}-{settings.max_emails_per_cycle}</div>
            <div className="text-gray-500 text-xs mt-1">random range</div>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="text-gray-600 text-xs mb-1">Email Delay</div>
            <div className="text-xl font-bold text-yellow-600">{settings.min_email_delay_minutes}-{settings.max_email_delay_minutes}</div>
            <div className="text-gray-500 text-xs mt-1">min gap</div>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="text-gray-600 text-xs mb-1">Office Hours</div>
            <div className="text-xl font-bold text-purple-600">{settings.office_start_hour}:00 - {settings.office_end_hour}:00</div>
            <div className="text-gray-500 text-xs mt-1">IST timezone</div>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="text-gray-600 text-xs mb-1">Cycle Interval</div>
            <div className="text-xl font-bold text-orange-600">{settings.min_cycle_interval_minutes}-{settings.max_cycle_interval_minutes}</div>
            <div className="text-gray-500 text-xs mt-1">minutes gap</div>
          </div>
        </div>
      </div>

      {/* Information Banner */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mx-4 sm:mx-0">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">Important Information</h3>
            <div className="mt-2 text-sm text-yellow-700">
              <ul className="list-disc list-inside space-y-1">
                <li><strong>All settings are stored in MongoDB</strong> and synced across all system components</li>
                <li><strong>Changes take effect immediately</strong> - next warmup cycle will use new settings</li>
                <li><strong>No hardcoded values</strong> - all scheduling controlled by these settings</li>
                <li>The scheduler automatically reads these values every cycle</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Email Configuration */}
      <div className="bg-white rounded-lg p-4 sm:p-6 card-shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Configuration</h3>
        <div className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Daily Cycle Limit
              </label>
              <input 
                type="number" 
                className="form-input" 
                value={settings.daily_cycle_limit}
                onChange={(e) => handleInputChange('daily_cycle_limit', e.target.value)}
                min="1"
                max="10"
              />
              <p className="text-xs text-gray-500 mt-1">
                Maximum warmup cycles per day (Recommended: 3-5 cycles)
              </p>
            </div>

          </div>
          
          {/* Warmup Range Configuration */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Emails Per Cycle</label>
              <input 
                type="number" 
                className="form-input" 
                value={settings.min_emails_per_cycle}
                onChange={(e) => handleInputChange('min_emails_per_cycle', e.target.value)}
                min="1"
                max="50"
              />
              <p className="text-xs text-gray-500 mt-1">Minimum emails to send per warmup cycle</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Emails Per Cycle</label>
              <input 
                type="number" 
                className="form-input" 
                value={settings.max_emails_per_cycle}
                onChange={(e) => handleInputChange('max_emails_per_cycle', e.target.value)}
                min="1"
                max="100"
              />
              <p className="text-xs text-gray-500 mt-1">Maximum emails to send per warmup cycle</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Office Start Hour</label>
              <select 
                className="form-select"
                value={settings.office_start_hour}
                onChange={(e) => handleInputChange('office_start_hour', e.target.value)}
              >
                <option value="1">1 AM</option>
                <option value="2">2 AM</option>
                <option value="3">3 AM</option>
                <option value="4">4 AM</option>
                <option value="5">5 AM</option>
                <option value="6">6 AM</option>
                <option value="7">7 AM</option>
                <option value="8">8 AM</option>
                <option value="9">9 AM</option>
                <option value="10">10 AM</option>
                <option value="11">11 AM</option>
                <option value="12">12 PM</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Warmup cycles will only run during office hours</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Office End Hour</label>
              <select 
                className="form-select"
                value={settings.office_end_hour}
                onChange={(e) => handleInputChange('office_end_hour', e.target.value)}
              >
                <option value="14">2 PM</option>
                <option value="15">3 PM</option>
                <option value="16">4 PM</option>
                <option value="17">5 PM</option>
                <option value="18">6 PM</option>
                <option value="19">7 PM</option>
                <option value="20">8 PM</option>
                <option value="21">9 PM</option>
                <option value="22">10 PM</option>
                <option value="23">11 PM</option>
                <option value="24">12 AM (Midnight)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">No warmup cycles after this hour</p>
            </div>
          </div>
          
          {/* Email Sending Delay Configuration */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
            <h4 className="text-sm font-semibold text-yellow-900 mb-3">⏱️ Human-Like Email Delays</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Delay Between Emails (minutes)</label>
                <input 
                  type="number" 
                  className="form-input" 
                  value={settings.min_email_delay_minutes}
                  onChange={(e) => handleInputChange('min_email_delay_minutes', e.target.value)}
                  min="1"
                  max="30"
                />
                <p className="text-xs text-gray-500 mt-1">Minimum wait time before sending next email (3-5 min recommended)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Delay Between Emails (minutes)</label>
                <input 
                  type="number" 
                  className="form-input" 
                  value={settings.max_email_delay_minutes}
                  onChange={(e) => handleInputChange('max_email_delay_minutes', e.target.value)}
                  min="1"
                  max="30"
                />
                <p className="text-xs text-gray-500 mt-1">Maximum wait time before sending next email (3-5 min recommended)</p>
              </div>
            </div>
            <div className="mt-3 text-xs text-yellow-700">
              <strong>⚠️ Important:</strong> Delays shorter than 3 minutes may appear robotic and trigger spam filters.
              Real humans don't send emails every 30 seconds!
            </div>
          </div>

          {/* Automatic Timing Configuration */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Cycle Interval (minutes)</label>
              <input 
                type="number" 
                className="form-input" 
                value={settings.min_cycle_interval_minutes}
                onChange={(e) => handleInputChange('min_cycle_interval_minutes', e.target.value)}
                min="10"
                max="300"
              />
              <p className="text-xs text-gray-500 mt-1">Minimum time between automatic warmup cycles</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Cycle Interval (minutes)</label>
              <input 
                type="number" 
                className="form-input" 
                value={settings.max_cycle_interval_minutes}
                onChange={(e) => handleInputChange('max_cycle_interval_minutes', e.target.value)}
                min="30"
                max="480"
              />
              <p className="text-xs text-gray-500 mt-1">Maximum time between automatic warmup cycles</p>
            </div>
          </div>
        </div>
      </div>

      {/* Reply System Configuration */}
      <div className="bg-white rounded-lg p-6 card-shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Reply System Configuration</h3>
        <div className="space-y-4">
          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              id="enable_reply_processing"
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              checked={settings.enable_reply_processing}
              onChange={(e) => handleInputChange('enable_reply_processing', e.target.checked)}
            />
            <label htmlFor="enable_reply_processing" className="ml-2 block text-sm text-gray-900">
              Enable Automatic Reply Processing
            </label>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reply Percentage (%)</label>
              <div className="relative">
                <input 
                  type="number" 
                  className="form-input" 
                  value={settings.reply_probability}
                  onChange={(e) => handleInputChange('reply_probability', e.target.value)}
                  min="0"
                  max="100"
                  disabled={!settings.enable_reply_processing}
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">%</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Percentage of emails that will receive automated replies (30-40% recommended)</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Replies Per Cycle</label>
              <input 
                type="number" 
                className="form-input" 
                value={settings.max_replies_per_cycle}
                onChange={(e) => handleInputChange('max_replies_per_cycle', e.target.value)}
                min="1"
                max="10"
                disabled={!settings.enable_reply_processing}
              />
              <p className="text-xs text-gray-500 mt-1">Maximum automated replies per warmup cycle</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Reply Delay (minutes)</label>
              <input 
                type="number" 
                className="form-input" 
                value={settings.min_reply_delay_minutes}
                onChange={(e) => handleInputChange('min_reply_delay_minutes', e.target.value)}
                min="10"
                max="300"
                disabled={!settings.enable_reply_processing}
              />
              <p className="text-xs text-gray-500 mt-1">Minimum delay before sending automated replies (human-like timing)</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Reply Delay (minutes)</label>
              <input 
                type="number" 
                className="form-input" 
                value={settings.max_reply_delay_minutes}
                onChange={(e) => handleInputChange('max_reply_delay_minutes', e.target.value)}
                min="30"
                max="480"
                disabled={!settings.enable_reply_processing}
              />
              <p className="text-xs text-gray-500 mt-1">Maximum delay before sending automated replies (maintains realism)</p>
            </div>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h4 className="text-sm font-medium text-blue-800">Reply System Info</h4>
                <div className="mt-2 text-sm text-blue-700">
                  <ul className="list-disc list-inside space-y-1">
                    <li>30-40% reply rate is recommended for natural warmup behavior</li>
                    <li>Human-like delays (30-180 min) prevent robotic detection</li>
                    <li>Replies use static templates - no AI generation</li>
                    <li>All replies are tracked in the dashboard metrics</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SPF/DKIM/DMARC Configuration */}
      <div className="bg-white rounded-lg p-6 card-shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">DNS Authentication Setup</h3>
        <div className="space-y-6">
          <div className="border-l-4 border-blue-500 bg-blue-50 p-4">
            <h4 className="font-semibold text-blue-900">SPF Record</h4>
            <p className="text-sm text-blue-800 mt-1">Add this TXT record to your DNS:</p>
            <code className="block bg-blue-100 p-2 mt-2 rounded text-sm font-mono">
              v=spf1 include:_spf.google.com ~all
            </code>
          </div>
          
          <div className="border-l-4 border-green-500 bg-green-50 p-4">
            <h4 className="font-semibold text-green-900">DKIM Setup</h4>
            <p className="text-sm text-green-800 mt-1">Configure in Google Workspace Admin Console</p>
            <ol className="text-sm text-green-800 mt-2 space-y-1 list-decimal list-inside">
              <li>Go to Gmail → Authenticate email</li>
              <li>Generate new record</li>
              <li>Add TXT record to DNS</li>
              <li>Start authentication</li>
            </ol>
          </div>
          
          <div className="border-l-4 border-purple-500 bg-purple-50 p-4">
            <h4 className="font-semibold text-purple-900">DMARC Policy</h4>
            <p className="text-sm text-purple-800 mt-1">Start with monitoring mode:</p>
            <code className="block bg-purple-100 p-2 mt-2 rounded text-sm font-mono">
              v=DMARC1; p=none; rua=mailto:dmarc-reports@yourdomain.com;
            </code>
          </div>
        </div>
      </div>

      {/* System Information */}
      <div className="bg-white rounded-lg p-6 card-shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">System Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Application</h4>
            <div className="space-y-1 text-sm text-gray-600">
              <div>Version: 4.0.0</div>
              <div>Frontend: React.js</div>
              <div>Backend: FastAPI</div>
              <div>Database: MongoDB Atlas</div>
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Features</h4>
            <div className="space-y-1 text-sm text-gray-600">
              <div>✅ Multi-domain support</div>
              <div>✅ A/B testing framework</div>
              <div>✅ Real-time monitoring</div>
              <div>✅ MongoDB Atlas integration</div>
            </div>
          </div>
        </div>
      </div>

      {/* Persistent Scheduler Management */}
      <div className="bg-white rounded-lg p-6 card-shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Persistent Scheduler Management</h3>
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h4 className="font-medium text-gray-900 mb-2">Scheduled Email Queue</h4>
              <p className="text-sm text-gray-600 mb-4">
                Manage persistent scheduled emails. This queue survives system restarts and contains emails waiting to be sent.
              </p>
              
              {schedulerStatus && (
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Total Scheduled:</span>
                      <div className="text-lg font-bold text-blue-600">{schedulerStatus.total_scheduled}</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Ready to Send:</span>
                      <div className="text-lg font-bold text-green-600">{schedulerStatus.ready_to_send}</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Overdue:</span>
                      <div className="text-lg font-bold text-red-600">{schedulerStatus.overdue_count}</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Storage File:</span>
                      <div className={`text-lg font-bold ${schedulerStatus.file_exists ? 'text-green-600' : 'text-gray-400'}`}>
                        {schedulerStatus.file_exists ? 'Exists' : 'None'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <button
              onClick={fetchSchedulerStatus}
              disabled={loadingScheduler}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingScheduler ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh Status
                </>
              )}
            </button>
            
            <button
              onClick={handleClearScheduler}
              disabled={clearingScheduler || (schedulerStatus && schedulerStatus.total_scheduled === 0)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {clearingScheduler ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Clearing...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Clear All Scheduled Emails
                </>
              )}
            </button>
          </div>
          
          {schedulerStatus && schedulerStatus.total_scheduled === 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex">
                <svg className="w-5 h-5 text-green-400 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div className="text-sm text-green-700">
                  <p className="font-medium">Queue is empty</p>
                  <p>No scheduled emails in the persistent queue.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Settings Status */}
      {lastUpdated && (
        <div className="text-right mb-4">
          <p className="text-xs text-gray-500">
            Settings last loaded: {lastUpdated.toLocaleTimeString()}
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end space-x-4">
        <button 
          className="btn-secondary flex items-center space-x-2" 
          onClick={handleRefresh}
          disabled={loading}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span>{loading ? 'Refreshing...' : 'Refresh Settings'}</span>
        </button>
        
        <button 
          className="btn-primary" 
          onClick={handleSave}
          disabled={saving || loading}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default Settings;
